/* FILE: cn-card-conditions.js */
(() => {
    const section = document.querySelector('.cn-condition-page');
    if (!section) return;

    const textScrollers = Array.from(
      section.querySelectorAll('[data-cn-condition-text-scroll]')
    );
    const mobileQuery = window.matchMedia('(max-width: 749px)');

    const updateTextScrollState = (scroller) => {
      if (!mobileQuery.matches) {
        scroller.classList.remove('has-overflow', 'is-at-start', 'is-at-end');
        return;
      }

      const tolerance = 2;
      const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      const hasOverflow = maxScrollTop > tolerance;

      scroller.classList.toggle('has-overflow', hasOverflow);
      scroller.classList.toggle('is-at-start', !hasOverflow || scroller.scrollTop <= tolerance);
      scroller.classList.toggle(
        'is-at-end',
        !hasOverflow || scroller.scrollTop >= maxScrollTop - tolerance
      );
    };

    const updateAllTextScrollers = () => {
      textScrollers.forEach(updateTextScrollState);
    };

    textScrollers.forEach((scroller) => {
      let frame = null;

      scroller.addEventListener('scroll', () => {
        if (frame !== null) return;

        frame = window.requestAnimationFrame(() => {
          updateTextScrollState(scroller);
          frame = null;
        });
      }, { passive: true });
    });

    if ('ResizeObserver' in window) {
      const textResizeObserver = new ResizeObserver(updateAllTextScrollers);
      textScrollers.forEach((scroller) => textResizeObserver.observe(scroller));
    } else {
      window.addEventListener('resize', updateAllTextScrollers, { passive: true });
    }

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', updateAllTextScrollers);
    } else {
      mobileQuery.addListener(updateAllTextScrollers);
    }

    window.requestAnimationFrame(updateAllTextScrollers);

    const modal = document.querySelector('[data-cn-condition-modal]');
    const modalImage = modal?.querySelector('[data-cn-condition-modal-image]');
    const modalCaption = modal?.querySelector('[data-cn-condition-modal-caption]');
    const imageButtons = Array.from(
      section?.querySelectorAll('[data-cn-condition-image-button]') || []
    );
    const modalCloseButtons = modal
      ? Array.from(modal.querySelectorAll('[data-cn-condition-modal-close]'))
      : [];

    if (!modal || !modalImage) return;

    let lastFocusedElement = null;
    let modalTimer = null;
    let activePreload = false;
    const queuedPreloadSources = new Set();
    const preloadQueue = [];

    const runPreloadQueue = () => {
      if (activePreload || !preloadQueue.length) return;

      const imageSrc = preloadQueue.shift();
      const preloadImage = new Image();
      activePreload = true;
      preloadImage.decoding = 'async';
      preloadImage.fetchPriority = 'low';

      const finishPreload = () => {
        activePreload = false;
        window.setTimeout(runPreloadQueue, 80);
      };

      preloadImage.addEventListener('load', finishPreload, { once: true });
      preloadImage.addEventListener('error', finishPreload, { once: true });
      preloadImage.src = imageSrc;
    };

    const queueModalImage = (trigger, prioritize = false) => {
      const imageSrc = trigger?.dataset.cnConditionImageSrc || '';
      if (!imageSrc || queuedPreloadSources.has(imageSrc)) return;

      queuedPreloadSources.add(imageSrc);

      if (prioritize) {
        preloadQueue.unshift(imageSrc);
      } else {
        preloadQueue.push(imageSrc);
      }

      runPreloadQueue();
    };

    const startViewportPreloading = () => {
      if (!imageButtons.length) return;

      if (!('IntersectionObserver' in window)) {
        imageButtons.slice(0, 3).forEach((button) => queueModalImage(button));
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          queueModalImage(entry.target);
        });
      }, {
        rootMargin: '320px 0px',
        threshold: 0.01
      });

      imageButtons.forEach((button) => observer.observe(button));
    };

    const scheduleViewportPreloading = () => {
      const schedule = () => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(startViewportPreloading, { timeout: 1800 });
        } else {
          window.setTimeout(startViewportPreloading, 500);
        }
      };

      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true });
      }
    };

    section.addEventListener('pointerover', (event) => {
      const trigger = event.target.closest?.('[data-cn-condition-image-button]');
      if (trigger) queueModalImage(trigger, true);
    }, { passive: true });

    section.addEventListener('focusin', (event) => {
      const trigger = event.target.closest?.('[data-cn-condition-image-button]');
      if (trigger) queueModalImage(trigger, true);
    });

    scheduleViewportPreloading();

    const lockPage = () => {
      document.documentElement.classList.add('cn-condition-modal-open');
      document.body.classList.add('cn-condition-modal-open');
    };

    const unlockPage = () => {
      document.documentElement.classList.remove('cn-condition-modal-open');
      document.body.classList.remove('cn-condition-modal-open');
    };

    const openModal = (trigger) => {
      const imageSrc = trigger.dataset.cnConditionImageSrc || '';
      const imageAlt = trigger.dataset.cnConditionImageAlt || '';
      const imageCaption = trigger.dataset.cnConditionImageCaption || '';

      if (!imageSrc) return;
      if (modalTimer) window.clearTimeout(modalTimer);

      lastFocusedElement = trigger;
      queueModalImage(trigger, true);
      modalImage.src = imageSrc;
      modalImage.alt = imageAlt;

      if (modalCaption) {
        modalCaption.textContent = imageCaption;
        modalCaption.hidden = imageCaption.trim() === '';
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      lockPage();

      window.requestAnimationFrame(() => {
        modal.classList.add('is-open');
        const closeButton = modal.querySelector('.cn-condition-modal__close');
        if (closeButton) closeButton.focus({ preventScroll: true });
      });
    };

    const closeModal = () => {
      if (modal.hidden) return;

      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      unlockPage();

      modalTimer = window.setTimeout(() => {
        modal.hidden = true;
        modalImage.removeAttribute('src');
        modalImage.alt = '';

        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
          lastFocusedElement.focus({ preventScroll: true });
        }

        lastFocusedElement = null;
      }, 160);
    };

    section.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-cn-condition-image-button]');
      if (!trigger || !section.contains(trigger)) return;
      openModal(trigger);
    });

    modalCloseButtons.forEach((button) => {
      button.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });
  })();
